// frontend/src/features/equipment/EquipmentSetupWizard/steps/CompletionStep.tsx
import { Box, Button, Card, Center, Group, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconCheck, IconList, IconSettings } from '@tabler/icons-react';

interface CompletionStepProps {
  /** Number of items saved */
  savedCount: number;
  /** Callback when "Done" is clicked */
  onClose: () => void;
  /** Callback when "Go to Equipment" is clicked */
  onGoToEquipment: () => void;
}

/**
 * Completion step shown after equipment is saved.
 * Shows success message and next steps options.
 */
export function CompletionStep({ savedCount, onClose, onGoToEquipment }: CompletionStepProps) {
  return (
    <Stack gap="xl" align="center" data-testid="completion-step">
      <Center>
        <ThemeIcon size={80} radius="xl" color="green" variant="light" data-testid="success-icon">
          <IconCheck size={48} />
        </ThemeIcon>
      </Center>

      <Stack gap="xs" align="center">
        <Title order={3}>Equipment setup complete!</Title>
        <Text c="dimmed">
          {savedCount} {savedCount === 1 ? 'item' : 'items'} added to your profile.
        </Text>
      </Stack>

      <Card withBorder w="100%">
        <Stack gap="md">
          <Title order={5}>What's next?</Title>

          <Group gap="md" grow>
            <Card withBorder p="sm">
              <Stack gap="xs" align="center">
                <ThemeIcon size="lg" variant="light">
                  <IconSettings size={20} />
                </ThemeIcon>
                <Text size="sm" ta="center" fw={500}>
                  Add More Details
                </Text>
                <Text size="xs" c="dimmed" ta="center">
                  Purchase dates, serial numbers, warranty info
                </Text>
                <Button
                  size="xs"
                  variant="light"
                  onClick={onGoToEquipment}
                  fullWidth
                >
                  Go to Equipment
                </Button>
              </Stack>
            </Card>

            <Card withBorder p="sm">
              <Stack gap="xs" align="center">
                <ThemeIcon size="lg" variant="light">
                  <IconList size={20} />
                </ThemeIcon>
                <Text size="sm" ta="center" fw={500}>
                  Set Up Tasks
                </Text>
                <Text size="xs" c="dimmed" ta="center">
                  Maintenance schedules for your equipment
                </Text>
                <Button size="xs" variant="light" disabled fullWidth>
                  Coming Soon
                </Button>
              </Stack>
            </Card>
          </Group>
        </Stack>
      </Card>

      <Box w="100%">
        <Button variant="outline" onClick={onClose} fullWidth>
          Done
        </Button>
      </Box>
    </Stack>
  );
}
